import { LayoutSize } from '@/contexts/layout-context';
import { ViewStyle } from 'react-native';

const CARD_VIEW_V2_GRID_CARD_MIN_WIDTH = 175;
const CARD_VIEW_V2_GRID_CARD_MEDIUM_WIDTH = 240;
/** Max width for product cards in v2 grid layouts */
const CARD_VIEW_V2_GRID_CARD_MAX_WIDTH = 280;
const CARD_VIEW_V2_GRID_MAX_COLUMN_COUNT = 6;
const CARD_VIEW_V2_GRID_COLUMN_GAP = 16;
const CARD_VIEW_V2_GRID_DESKTOP_WIDE_COLUMN_COUNT = 5;
const CARD_VIEW_V2_GRID_CELL_HORIZONTAL_PADDING =
  CARD_VIEW_V2_GRID_COLUMN_GAP / 2;

/** Per-column cell padding so adjacent cells sum to `CARD_VIEW_V2_GRID_COLUMN_GAP` */
export const CARD_VIEW_V2_GRID_CELL_LIST_ITEM_STYLE: ViewStyle = {
  paddingHorizontal: CARD_VIEW_V2_GRID_CELL_HORIZONTAL_PADDING,
  paddingVertical: 0,
};

const CARD_VIEW_V2_GRID_EDGE_INSET =
  CARD_VIEW_V2_GRID_CELL_HORIZONTAL_PADDING * 2;
const CARD_VIEW_V2_GRID_DESKTOP_WIDE_MAX_WIDTH =
  CARD_VIEW_V2_GRID_CARD_MAX_WIDTH *
    CARD_VIEW_V2_GRID_DESKTOP_WIDE_COLUMN_COUNT +
  CARD_VIEW_V2_GRID_COLUMN_GAP *
    (CARD_VIEW_V2_GRID_DESKTOP_WIDE_COLUMN_COUNT - 1) +
  CARD_VIEW_V2_GRID_EDGE_INSET;

const CARD_VIEW_V2_GRID_ULTRA_WIDE_MIN_COMFORTABLE_WIDTH =
  CARD_VIEW_V2_GRID_CARD_MEDIUM_WIDTH * CARD_VIEW_V2_GRID_MAX_COLUMN_COUNT +
  CARD_VIEW_V2_GRID_COLUMN_GAP * (CARD_VIEW_V2_GRID_MAX_COLUMN_COUNT - 1) +
  CARD_VIEW_V2_GRID_EDGE_INSET;
const CARD_VIEW_V2_GRID_ULTRA_WIDE_MIN_WIDTH = Math.max(
  CARD_VIEW_V2_GRID_DESKTOP_WIDE_MAX_WIDTH,
  CARD_VIEW_V2_GRID_ULTRA_WIDE_MIN_COMFORTABLE_WIDTH,
);
const CARD_VIEW_V2_GRID_CONTENT_MAX_WIDTH =
  CARD_VIEW_V2_GRID_CARD_MAX_WIDTH * CARD_VIEW_V2_GRID_MAX_COLUMN_COUNT +
  CARD_VIEW_V2_GRID_COLUMN_GAP * (CARD_VIEW_V2_GRID_MAX_COLUMN_COUNT - 1) +
  CARD_VIEW_V2_GRID_EDGE_INSET;

export const CARD_VIEW_V2_GRID_LIST_ITEM_STYLE: ViewStyle = {
  alignSelf: 'center',
  marginHorizontal: 0,
  marginVertical: 8,
  maxWidth: CARD_VIEW_V2_GRID_CARD_MAX_WIDTH,
  minWidth: CARD_VIEW_V2_GRID_CARD_MIN_WIDTH,
  width: '100%',
};

/**
 * Centers max-width grid content on very wide viewports.
 * Uses horizontal padding (not only maxWidth + margin auto) because FlashList /
 * scroll content layout on web/native does not reliably center the same way as CSS flex alone.
 */
export const getCardViewV2GridListContentContainerStyle = (
  layoutSize: LayoutSize,
  availableWidth: number,
): ViewStyle => {
  const baseHorizontalInset =
    layoutSize === LayoutSize.SMALL
      ? CARD_VIEW_V2_GRID_CELL_HORIZONTAL_PADDING
      : 0;
  const contentHorizontalInset = Math.max(
    0,
    (availableWidth - CARD_VIEW_V2_GRID_CONTENT_MAX_WIDTH) / 2,
  );

  return {
    paddingHorizontal: contentHorizontalInset + baseHorizontalInset,
    width: '100%',
  };
};

// Fixed widths for `MinimalOrderableItemListItemV2` in horizontal carousels and etc
export const CARD_VIEW_V2_WIDTH_BY_LAYOUT_SIZE: {
  [size in LayoutSize]: number;
} = {
  [LayoutSize.SMALL]: CARD_VIEW_V2_GRID_CARD_MIN_WIDTH,
  [LayoutSize.SMALL_TABLET]: CARD_VIEW_V2_GRID_CARD_MEDIUM_WIDTH,
  [LayoutSize.MEDIUM]: CARD_VIEW_V2_GRID_CARD_MEDIUM_WIDTH,
  [LayoutSize.LARGE]: CARD_VIEW_V2_GRID_CARD_MAX_WIDTH,
};

const CARD_VIEW_V2_GRID_COLUMN_COUNT_BY_LAYOUT_SIZE: {
  [size in LayoutSize]: number;
} = {
  [LayoutSize.SMALL]: 2,
  [LayoutSize.SMALL_TABLET]: 3,
  [LayoutSize.MEDIUM]: 4,
  [LayoutSize.LARGE]: 5,
};

export const getCardViewV2GridColumnCount = (
  layoutSize: LayoutSize,
  availableWidth: number,
) => {
  if (
    layoutSize === LayoutSize.LARGE &&
    availableWidth >= CARD_VIEW_V2_GRID_ULTRA_WIDE_MIN_WIDTH
  ) {
    return CARD_VIEW_V2_GRID_MAX_COLUMN_COUNT;
  }

  return CARD_VIEW_V2_GRID_COLUMN_COUNT_BY_LAYOUT_SIZE[layoutSize];
};
